import type { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';
import { useUploadFile } from '~/hooks/queries/use-files';
import { uploadGateState } from './design-direction-gate';
import { DESIGN_REFERENCE_IMAGE_TYPES, MAX_DESIGN_UPLOADS, type DesignDirectionDraft } from './design-direction';
import type { UploadInFlight } from './design-direction-section';

/**
 * FRONT-2382 review: `DesignDirectionDisclosure`'s reference-image upload
 * flow — the 5-upload cap gate, queueing a dropped/picked batch, and
 * running the uploads themselves — pulled out into its own hook so that
 * component is rendering only. Logic unchanged from the original inline
 * version (see the queueing comment below).
 */
export function useDesignReferenceUploads({
  value,
  onChange,
  uploading,
  onUploadingChange,
}: {
  value: DesignDirectionDraft;
  onChange: Dispatch<SetStateAction<DesignDirectionDraft>>;
  uploading: UploadInFlight[];
  onUploadingChange: Dispatch<SetStateAction<UploadInFlight[]>>;
}) {
  const upload = useUploadFile();
  const gate = uploadGateState({ uploadCount: value.uploads.length + uploading.length });

  async function uploadFiles(files: File[]) {
    const accepted = files.filter((f) =>
      (DESIGN_REFERENCE_IMAGE_TYPES as readonly string[]).includes(f.type),
    );
    if (accepted.length === 0) return;
    const remaining = Math.max(0, MAX_DESIGN_UPLOADS - value.uploads.length - uploading.length);
    // FRONT-2382 review #3: every accepted file is queued into `uploading`
    // BEFORE any request goes out, so the 5-cap counts a burst of dropped
    // files immediately rather than one at a time as each prior upload
    // happens to finish — the old shape awaited each file in turn, so only
    // ONE was ever "in flight" and a fast 8-file drop could slip past the
    // cap while requests were still queued behind it. Uploads then run in
    // parallel, each removing only its own entry on settle.
    const queued = accepted.slice(0, remaining).map((file) => ({ file, localId: crypto.randomUUID() }));
    if (queued.length === 0) return;
    onUploadingChange((xs) => [...xs, ...queued.map(({ file, localId }) => ({ localId, name: file.name }))]);
    await Promise.all(
      queued.map(async ({ file, localId }) => {
        try {
          const row = await upload.mutateAsync({ file, name: file.name });
          onChange((prev) => ({
            ...prev,
            uploads: [...prev.uploads, { fileId: row.id, name: row.name, contentType: row.contentType }],
          }));
        } catch (err) {
          toast.error((err as Error).message || `Could not upload ${file.name}`);
        } finally {
          onUploadingChange((xs) => xs.filter((u) => u.localId !== localId));
        }
      }),
    );
  }

  function onRemove(fileId: string) {
    onChange((prev) => ({ ...prev, uploads: prev.uploads.filter((x) => x.fileId !== fileId) }));
  }

  return { gate, onFiles: (files: File[]) => void uploadFiles(files), onRemove };
}
